/**
 * Unified Filter Evaluation Engine
 *
 * This module provides client-side evaluation of the new unified filter expressions,
 * replacing the old AssigneeFilter and ValidationExpression evaluation logic.
 *
 * **A `true` from this evaluator is not authorization (OQ-8, EAINT-908 Phase 1).**
 * Gap A made lane-leaf evaluation (`evaluatePerspectiveLeafSync` /
 * `evaluateRelationshipLeafSync` / `evaluateArcLeafSync` below) real instead
 * of an unconditional `false`, so a caller now gets a genuine allow/deny
 * answer whenever `context` carries the resolved lane data. Every caller of
 * `evaluateFilterExpression`/`evaluateFilterGroup` in this codebase is a
 * display/predicate surface — approach applicability matching
 * (`applicabilityValidator.ts`, `checkApplicability.ts`, both answering "does
 * this sphere likely match", never "may this caller act") — not an access
 * decision. Permission enforcement lives in SpiceDB/Authzed
 * (`@center-platform/permissions`), never in this module. If a future caller
 * wires this evaluator's result into a permission check, that caller is
 * wrong, not this evaluator — do not lose the fail-closed rationale below by
 * treating an unresolvable lane leaf's `false` as "safe to skip".
 */

import { ArcFilter, FilterExpression, FilterGroup, FilterGroupLeaf, FilterOperator, PerspectiveFilter, RelationshipFilter } from 'types/filters';
import type { SchemaProperty } from 'types/schema';
import { compareValues, expectedOperand } from './operators';
import { isArcLeaf, isLaneLeaf, isPerspectiveLeaf, isPropertyLeaf, isRelationshipLeaf } from './filterGroupLeaf';
import { resolveFilterPropertyName } from './resolveFilterPropertyName';

/** Structured warning sink for lane-leaf evaluation gaps. Mirrors `FilterVariableLogger`. */
export interface FilterEvaluationLogger {
  warn(obj: Record<string, unknown>, msg: string): void;
}

/**
 * Reads a dot-notation path off `context`, same lookup `getNestedProperty`
 * uses below (declared later in this file; duplicated here as a narrow local
 * helper would create a forward-reference — see the shared implementation
 * at the bottom for the canonical version consumed by both).
 */
function readPath(obj: any, path: string): any {
  return getNestedProperty(obj, path);
}

/**
 * Evaluates a perspective lane leaf against a `context` object that mirrors
 * the search-index sphere document shape (`assigned_perspectives.<name>`,
 * see `transformSphere.ts` / `buildElasticsearchQuery.ts`) — the one shape
 * this synchronous, in-memory evaluator can resolve without a graph
 * traversal. Absent that field, fails closed with a structured warning
 * naming the lane and the reason (Gap A).
 */
function evaluatePerspectiveLeafSync(
  filter: PerspectiveFilter,
  context: Record<string, any>,
  log?: FilterEvaluationLogger
): boolean {
  const raw = readPath(context, `assigned_perspectives.${filter.perspectiveSystemName}`);
  if (raw === undefined) {
    log?.warn(
      { lane: 'perspective', perspectiveSystemName: filter.perspectiveSystemName },
      '[filterEvaluation] No assigned_perspectives data in context — evaluating false (fail-closed)'
    );
    return false;
  }
  const assignedIds: unknown[] = Array.isArray(raw) ? raw : [];
  const hasAssignees = Array.isArray(filter.assignees) && filter.assignees.length > 0;
  const matchCount = hasAssignees
    ? assignedIds.filter((id) => filter.assignees!.includes(id as string)).length
    : assignedIds.length;

  if (filter.operator === 'not_in') {
    return matchCount === 0;
  }
  return matchCount >= (filter.minCount ?? 1);
}

/**
 * Evaluates a relationship lane leaf against `assigned_relationships_out.<name>`
 * / `assigned_relationships_in.<name>` on `context` (same sphere-document
 * shape as above). Absent both fields, fails closed with a warning (Gap A).
 */
function evaluateRelationshipLeafSync(
  filter: RelationshipFilter,
  context: Record<string, any>,
  log?: FilterEvaluationLogger
): boolean {
  const outRaw = readPath(context, `assigned_relationships_out.${filter.relationshipSystemName}`);
  const inRaw = readPath(context, `assigned_relationships_in.${filter.relationshipSystemName}`);
  if (outRaw === undefined && inRaw === undefined) {
    log?.warn(
      { lane: 'relationship', relationshipSystemName: filter.relationshipSystemName },
      '[filterEvaluation] No assigned_relationships data in context — evaluating false (fail-closed)'
    );
    return false;
  }

  const outIds: unknown[] = Array.isArray(outRaw) ? outRaw : [];
  const inIds: unknown[] = Array.isArray(inRaw) ? inRaw : [];
  const direction = filter.direction ?? 'both';
  let candidateIds: unknown[];
  if (direction === 'source') candidateIds = outIds;
  else if (direction === 'target') candidateIds = inIds;
  else candidateIds = [...outIds, ...inIds];

  const hasRelatedTo = Array.isArray(filter.relatedTo) && filter.relatedTo.length > 0;
  const matchCount = hasRelatedTo
    ? candidateIds.filter((id) => filter.relatedTo!.includes(id as string)).length
    : candidateIds.length;

  if (filter.operator === 'not_in') {
    return matchCount === 0;
  }
  return matchCount >= (filter.minCount ?? 1);
}

/**
 * Evaluates an arc lane leaf against `assigned_arcs` on `context` (same
 * sphere-document shape as above — see `transformSphere.ts`). Absent the
 * field, fails closed with a warning (Gap A).
 */
function evaluateArcLeafSync(
  filter: ArcFilter,
  context: Record<string, any>,
  log?: FilterEvaluationLogger
): boolean {
  const raw = readPath(context, 'assigned_arcs');
  if (raw === undefined) {
    log?.warn({ lane: 'arc' }, '[filterEvaluation] No assigned_arcs data in context — evaluating false (fail-closed)');
    return false;
  }

  const arcs: any[] = Array.isArray(raw) ? raw : [];
  const matching = arcs.filter((arc) => {
    if (filter.arcSelector === 'primary' && arc?.isPrimary !== true) return false;
    if (filter.templateSystemName && arc?.templateSystemName !== filter.templateSystemName) return false;
    if (filter.stageId?.length && !filter.stageId.includes(arc?.currentStageId)) return false;
    if (filter.healthStatus?.length && !filter.healthStatus.includes(arc?.healthStatus)) return false;
    if (filter.lifecycle?.length && !filter.lifecycle.includes(arc?.lifecycle)) return false;
    return true;
  });

  if (filter.operator === 'not_in') {
    return matching.length === 0;
  }
  return matching.length >= (filter.minCount ?? 1);
}

/**
 * Evaluates a single `FilterGroup` leaf against a context object. A property
 * leaf evaluates as before (Gap A, before this change: every lane leaf
 * evaluated unconditionally `false`). A lane leaf now evaluates for real
 * when `context` carries the resolved shape the search-index sphere document
 * uses (`assigned_perspectives`/`assigned_relationships_out`/`_in`/`assigned_arcs`);
 * when that data is absent it fails closed with a structured warning naming
 * the lane and the reason, rather than a silent, unexplained `false`.
 *
 * Gap A enumeration table (which lane resolves from which `context` field;
 * anything else fails closed to `false` with the named reason):
 *
 * | Lane         | Resolvable from context field(s)                                | Otherwise                          |
 * |--------------|------------------------------------------------------------------|-------------------------------------|
 * | perspective  | `assigned_perspectives.<perspectiveSystemName>`                  | `false` — "No assigned_perspectives data in context" |
 * | relationship | `assigned_relationships_out.<name>` and/or `_in.<name>`           | `false` — "No assigned_relationships data in context" |
 * | arc          | `assigned_arcs` (array of `{ isPrimary, templateSystemName, currentStageId, healthStatus, lifecycle }`) | `false` — "No assigned_arcs data in context" |
 * | property     | `resolveFilterPropertyName` against `schemaProperties`, then a dot-path read off `context` | `false` (unresolvable `{ aspect, property }`) |
 *
 * See the module header for OQ-8 — a `true` here is a match, not a grant.
 */
function evaluateGroupLeaf(
  leaf: FilterGroupLeaf,
  context: Record<string, any>,
  schemaProperties: SchemaProperty[],
  log?: FilterEvaluationLogger
): boolean {
  if (isLaneLeaf(leaf)) {
    if (isPerspectiveLeaf(leaf)) return evaluatePerspectiveLeafSync(leaf.filter, context, log);
    if (isRelationshipLeaf(leaf)) return evaluateRelationshipLeafSync(leaf.filter, context, log);
    if (isArcLeaf(leaf)) return evaluateArcLeafSync(leaf.filter, context, log);
  }
  return evaluateFilterExpression(leaf, context, schemaProperties);
}

/**
 * Evaluates a single filter expression against a context object.
 *
 * `schemaProperties` is the merged inventory a `{ aspect, property }`
 * reference (EAINT-899) resolves against — the same shape
 * `getRequestSchemaProperties` produces on the backend. Optional and
 * defaulted to `[]` for backward compatibility: every existing caller passes
 * a bare `property` (no `aspect`), which `resolveFilterPropertyName` returns
 * unchanged without ever scanning the inventory.
 */
export function evaluateFilterExpression(
  expression: FilterExpression,
  context: Record<string, any>,
  schemaProperties: SchemaProperty[] = []
): boolean {
  const resolvedProperty = resolveFilterPropertyName(expression, schemaProperties);

  if (!resolvedProperty || !expression.operator) {
    return false;
  }

  const value = getNestedProperty(context, resolvedProperty);

  return compareValues(value, expression.operator, expectedOperand(expression), {
    min: expression.min,
    max: expression.max,
  });
}

/**
 * Evaluates a filter group with support for nested groups and AND/OR logic
 */
export function evaluateFilterGroup(
  group: FilterGroup,
  context: Record<string, any>,
  schemaProperties: SchemaProperty[] = [],
  log?: FilterEvaluationLogger
): boolean {
  if (!group.expressions && !group.groups) {
    return true; // Empty group evaluates to true
  }

  // Evaluate all leaves in this group
  const expressionResults = (group.expressions || []).map(leaf =>
    evaluateGroupLeaf(leaf, context, schemaProperties, log)
  );

  // Recursively evaluate nested groups
  const groupResults = (group.groups || []).map(subGroup =>
    evaluateFilterGroup(subGroup, context, schemaProperties, log)
  );

  // Combine all results
  const allResults = [...expressionResults, ...groupResults];

  if (allResults.length === 0) {
    return true; // Empty results default to true
  }

  // Apply the operator
  return group.operator === 'and'
    ? allResults.every(Boolean)
    : allResults.some(Boolean);
}

/**
 * Evaluates multiple filter expressions with a simple operator (for backwards compatibility)
 */
export function evaluateMultipleFilterExpressions(
  expressions: FilterExpression[],
  context: Record<string, any>,
  operator: 'and' | 'or' = 'or',
  schemaProperties: SchemaProperty[] = []
): boolean {
  if (expressions.length === 0) {
    return true;
  }

  const results = expressions.map(expr => evaluateFilterExpression(expr, context, schemaProperties));

  return operator === 'and'
    ? results.every(Boolean)
    : results.some(Boolean);
}

// Valid filter operators as defined in types/filters.d.ts
const VALID_OPERATORS: FilterOperator[] = [
  'equals', 'not_equals',
  'greater_than', 'less_than', 'greater_than_equals', 'less_than_equals',
  'contains', 'not_contains', 'contains_all', 'in', 'not_in', 'exists',
  'starts_with', 'ends_with',
  'is_true', 'is_false',
  'is_empty', 'is_not_empty',
  'between',
  'date_within_days', 'within_last_days', 'within_next_days',
  'last_7_days', 'last_30_days'
];

/**
 * A usable `between` bound: a finite number, or a non-empty string. Never
 * coerced — `NaN`, `Infinity`, `''`, `null` and `undefined` are all refused, so
 * a bad bound makes the whole expression invalid rather than becoming a
 * permissive default (`/standards/shared/filtering.md`, the operand-validity
 * half of the never-widen rule).
 */
function isBetweenBound(bound: unknown): boolean {
  if (typeof bound === 'number') return Number.isFinite(bound);
  return typeof bound === 'string' && bound.length > 0;
}

/**
 * Validates a filter expression structure
 */
export function validateFilterExpression(expression: FilterExpression): boolean {
  if (!expression.property || !expression.operator) {
    return false;
  }

  if (!VALID_OPERATORS.includes(expression.operator)) {
    return false;
  }

  // Validate required fields for specific operators
  switch (expression.operator) {
    case 'in':
    case 'not_in':
    case 'contains_all':
      return Array.isArray(expression.values) && expression.values.length > 0;

    case 'between':
      // A `between` bound is a finite number for a numeric property, or a
      // NON-EMPTY STRING for a date/datetime one — the canonical ISO 8601 UTC
      // wire form, or an unresolved rolling-date token (`@today`,
      // `@startOfQuarter`, `@today-30d`) that the server-side resolver rewrites
      // to that form before any engine sees it.
      //
      // Requiring `typeof === 'number'` here rejected every date range the
      // platform actually writes: `/standards/shared/filtering.md` has mandated
      // the ISO-string form since EAINT-931 Phase 7a, and this validator gates
      // `evaluateFilterExpression`, so a date `between` evaluated to `false`
      // for every sphere. Fail-closed, so nothing widened — but the filter was
      // simply broken, and `between @startOfQuarter .. @endOfQuarter` (the
      // headline rolling-date case) could never have matched.
      return isBetweenBound(expression.min) && isBetweenBound(expression.max);

    case 'exists':
    case 'not_exists':
    case 'is_true':
    case 'is_false':
    case 'is_empty':
    case 'is_not_empty':
    case 'last_7_days':
    case 'last_30_days':
      return true; // These don't require values

    default:
      return expression.value !== undefined;
  }
}

/**
 * Validates a filter expression and throws a detailed error if invalid.
 * Use this function when you need to fail fast with a descriptive error message.
 *
 * @param filter The filter object to validate
 * @param context Optional context for error messages (e.g., index, schema name, perspective name)
 * @returns The validated FilterExpression
 * @throws Error with a detailed message if the filter is invalid
 */
export function validateFilterExpressionStrict(
  filter: unknown,
  context?: {
    index?: number;
    schemaSystemName?: string;
    perspectiveSystemName?: string;
  }
): FilterExpression {
  const { index, schemaSystemName, perspectiveSystemName } = context || {};

  // Build context prefix for error messages
  let contextPrefix = 'Invalid filter: ';
  if (index !== undefined && schemaSystemName && perspectiveSystemName) {
    contextPrefix = `Invalid assignee_filter[${index}] in perspective '${perspectiveSystemName}' of schema '${schemaSystemName}': `;
  } else if (index !== undefined) {
    contextPrefix = `Invalid filter[${index}]: `;
  }

  if (!filter || typeof filter !== 'object') {
    throw new Error(
      `${contextPrefix}Filter must be an object, got ${typeof filter}.`
    );
  }

  const filterObj = filter as Record<string, unknown>;

  // Check required 'property' field
  const property = filterObj['property'];
  if (typeof property !== 'string' || !property.trim()) {
    throw new Error(
      `${contextPrefix}Missing or invalid 'property' field. Each filter must have a non-empty string 'property'.`
    );
  }

  // Check required 'operator' field
  const operator = filterObj['operator'];
  if (typeof operator !== 'string' || !operator.trim()) {
    throw new Error(
      `${contextPrefix}Missing 'operator' field. Each filter must have an 'operator' (e.g., 'equals', 'in', 'contains'). ` +
      `Found: ${JSON.stringify(filter)}`
    );
  }

  // Validate operator is a known type
  if (!VALID_OPERATORS.includes(operator as FilterOperator)) {
    throw new Error(
      `${contextPrefix}Unknown operator '${operator}'. Valid operators are: ${VALID_OPERATORS.join(', ')}.`
    );
  }

  const validatedFilter: FilterExpression = {
    property: filterObj['property'] as string,
    operator: filterObj['operator'] as FilterOperator,
    value: filterObj['value'],
    values: filterObj['values'] as any[] | undefined,
    min: filterObj['min'] as number | undefined,
    max: filterObj['max'] as number | undefined,
    message: filterObj['message'] as string | undefined,
  };

  // Validate operator-specific requirements
  switch (validatedFilter.operator) {
    case 'in':
    case 'not_in':
    case 'contains_all':
      if (!Array.isArray(validatedFilter.values) || validatedFilter.values.length === 0) {
        throw new Error(
          `${contextPrefix}Operator '${validatedFilter.operator}' requires a non-empty 'values' array.`
        );
      }
      break;

    case 'between':
      if (typeof validatedFilter.min !== 'number' || typeof validatedFilter.max !== 'number') {
        throw new TypeError(
          `${contextPrefix}Operator 'between' requires numeric 'min' and 'max' fields.`
        );
      }
      break;

    case 'exists':
    case 'is_true':
    case 'is_false':
    case 'is_empty':
    case 'is_not_empty':
    case 'last_7_days':
    case 'last_30_days':
      // These don't require values
      break;

    default:
      if (validatedFilter.value === undefined) {
        throw new Error(
          `${contextPrefix}Operator '${validatedFilter.operator}' requires a 'value' field.`
        );
      }
  }

  return validatedFilter;
}

/**
 * Validates an array of filter expressions and throws a detailed error if any are invalid.
 *
 * @param filters The array of filters to validate
 * @param context Optional context for error messages
 * @returns Array of validated FilterExpressions
 * @throws Error with a detailed message if any filter is invalid or if the array is empty
 */
export function validateFilterExpressionsStrict(
  filters: unknown,
  context?: {
    schemaSystemName?: string;
    perspectiveSystemName?: string;
    requireNonEmpty?: boolean;
  }
): FilterExpression[] {
  const { schemaSystemName, perspectiveSystemName, requireNonEmpty = false } = context || {};

  if (!Array.isArray(filters)) {
    const contextMsg = schemaSystemName && perspectiveSystemName
      ? ` in perspective '${perspectiveSystemName}' of schema '${schemaSystemName}'`
      : '';
    throw new Error(`Expected an array of filters${contextMsg}, got ${typeof filters}.`);
  }

  if (requireNonEmpty && filters.length === 0) {
    const contextMsg = schemaSystemName && perspectiveSystemName
      ? `Perspective '${perspectiveSystemName}' in schema '${schemaSystemName}' has no 'assignee_filter' defined. `
      : '';
    throw new Error(
      `${contextMsg}A valid filter array with at least one filter is required.`
    );
  }

  const validatedFilters: FilterExpression[] = [];
  for (let i = 0; i < filters.length; i++) {
    const validatedFilter = validateFilterExpressionStrict(filters[i], {
      index: i,
      schemaSystemName,
      perspectiveSystemName,
    });
    validatedFilters.push(validatedFilter);
  }

  return validatedFilters;
}

/**
 * Validates a filter group structure recursively
 */
export function validateFilterGroup(group: FilterGroup): boolean {
  if (!group.operator || (group.operator !== 'and' && group.operator !== 'or')) {
    return false;
  }

  // Validate all leaves in this group. A lane leaf (perspective/relationship/
  // arc) is validated by its own lane-specific rules elsewhere (not this
  // property-expression validator) — its presence alone is structurally
  // valid here.
  if (group.expressions) {
    for (const leaf of group.expressions) {
      if (isPropertyLeaf(leaf) && !validateFilterExpression(leaf)) {
        return false;
      }
    }
  }

  // Recursively validate nested groups
  if (group.groups) {
    for (const subGroup of group.groups) {
      if (!validateFilterGroup(subGroup)) {
        return false;
      }
    }
  }

  // Must have at least one expression or group
  return Boolean((group.expressions && group.expressions.length > 0) ||
         (group.groups && group.groups.length > 0));
}

/**
 * Helper function to get nested properties from objects (supports dot notation)
 */
function getNestedProperty(obj: any, path: string): any {
  if (!obj || !path) {
    return undefined;
  }

  // Handle simple property access first (most common case)
  if (!path.includes('.')) {
    return obj[path];
  }

  // Handle nested property access
  return path.split('.').reduce((current, key) => {
    return current && typeof current === 'object' ? current[key] : undefined;
  }, obj);
}

/**
 * Optimized filter evaluation with early termination
 */
export function evaluateFilterExpressionsFast(
  expressions: FilterExpression[],
  context: Record<string, any>,
  operator: 'and' | 'or' = 'or',
  schemaProperties: SchemaProperty[] = []
): boolean {
  if (expressions.length === 0) {
    return true;
  }

  for (const expr of expressions) {
    const result = evaluateFilterExpression(expr, context, schemaProperties);

    // Early termination for performance
    if (operator === 'or' && result) {
      return true; // Found a match in OR operation
    }
    if (operator === 'and' && !result) {
      return false; // Found a failure in AND operation
    }
  }

  // If we reach here:
  // - For AND: all expressions passed
  // - For OR: no expressions passed
  return operator === 'and';
}

/**
 * Type-safe helper to create filter expressions
 */
export function createFilterExpression(
  property: string,
  operator: FilterOperator,
  options: {
    value?: any;
    values?: any[];
    min?: number;
    max?: number;
    message?: string;
  } = {}
): FilterExpression {
  const expression: FilterExpression = {
    property,
    operator,
    ...options
  };

  if (!validateFilterExpression(expression)) {
    throw new Error(`Invalid filter expression: ${JSON.stringify(expression)}`);
  }

  return expression;
}

/**
 * Type-safe helper to create filter groups
 */
export function createFilterGroup(
  operator: 'and' | 'or',
  expressions: FilterGroupLeaf[] = [],
  groups: FilterGroup[] = []
): FilterGroup {
  const group: FilterGroup = {
    operator,
    expressions,
    groups
  };

  if (!validateFilterGroup(group)) {
    throw new Error(`Invalid filter group: ${JSON.stringify(group)}`);
  }

  return group;
}

/**
 * Utility function to convert simple filters to filter expressions
 * (for migration and backwards compatibility during transition)
 */
export function simpleFiltersToExpressions(
  filters: Array<{ property: string; value: any }>
): FilterExpression[] {
  if (!Array.isArray(filters)) {
    return [];
  }

  return filters
    .filter(filter => {
      // Filter out null, undefined, or malformed filter objects
      return filter &&
             typeof filter === 'object' &&
             typeof filter.property === 'string' &&
             filter.property.trim() !== '' &&
             filter.value !== undefined;
    })
    .map(filter => createFilterExpression(
      filter.property,
      'equals',
      { value: filter.value }
    ));
}